import React from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    ListItemIcon,
    Paper,
    Typography
} from '@mui/material';
import { Delete, Event, EventAvailable } from '@mui/icons-material';
import {useJourney} from '../../../../../api/Journeys/journey_queries';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import * as http from '../../../../../util/elemental_axios_client';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import FlowActionTriggerProfileEventForm from './FlowActionTriggerProfileEventForm';


const FlowTriggerFilter = (props) => {
    const queryClient = useQueryClient();
    const {journeyId: journeyIdString, hideSidebar} = props;
    const journeyId = parseInt(journeyIdString);




    const queryProfileEventTypes = useQuery({
        queryKey: ['profile-event-types'],
        queryFn: async () => {
            return await http.get('/profile/event-types?per_page=1000');
        }
    });

    const mutationAddFlowTriggerAction = useMutation({
        mutationFn: (payload) => {
            return http.put(`/flows/${journey.id}/action_triggers`, payload);
        },
        onSuccess: (data) => {
            // The response contains the entire flow object
            queryClient.setQueryData(['journeys', journeyId], data);
            queryClient.invalidateQueries(['journeys', journeyId]);
        }
    });

    const mutationDeleteFlowTriggerAction = useMutation({
        mutationFn: (flow_action_trigger_id) => {
            return http.remove(`/flows/${journey.id}/action_triggers/${flow_action_trigger_id}`);
        },
        onSuccess: (data) => {
            // The response contains the entire flow object
            queryClient.setQueryData(['journeys', journeyId], data);
            queryClient.invalidateQueries(['journeys', journeyId]);
        }
    });


    const {data: journey, isSuccess} = useJourney(journeyId);


    const profileEventTypes = queryProfileEventTypes?.data?.page?.map(eventType => ({
        value: eventType.id,
        label: eventType.name
    })) || [];


    if (!isSuccess) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100%',
                p: 3
            }}>
                <Typography variant='body1' color='text.secondary'>Loading triggers...</Typography>
            </Box>
        );
    }





    const handleSubmitNewActionTrigger = (payload) => {
        mutationAddFlowTriggerAction.mutate(payload);
    };

    const handleDeleteActionTrigger = (flow_action_trigger_id) => {
        mutationDeleteFlowTriggerAction.mutate(flow_action_trigger_id);
    };

    const activeTriggers = journey.flow_action_triggers
                        .filter(trigger => trigger.flow_travel_action_type === 'enter')
                        .map((trigger) => {
                            const eventType = profileEventTypes.find(type => 
                                type.value === trigger.event_query.profile_event_type_id
                            );
                            return {trigger, eventType};
                        })
        .sort((a, b) => (a.eventType?.label || '').localeCompare(b.eventType?.label || ''));

    const draftTriggers = journey.flow_action_triggers_draft
                        .filter(trigger => trigger.flow_travel_action_type === 'enter')
                        .map((trigger) => {
                            const eventType = profileEventTypes.find(type => 
                                type.value === trigger.event_query.profile_event_type_id
                            );
                            return {trigger, eventType};
                        })
        .sort((a, b) => (a.eventType?.label || '').localeCompare(b.eventType?.label || ''));

    return ( journey && 
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            flexGrow: 1,
            p: 3
        }}>
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant='h6' sx={{ mb: 1, fontWeight: 'medium', color: 'text.primary' }}>
                    when one of these events happens
                    </Typography>
                </Box>

                {/* Active Triggers Section */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box display='flex' alignItems='center' sx={{ mb: 2 }}>
                            <EventAvailable sx={{ mr: 1, color: 'success.main' }} />
                            <Typography variant='h6' sx={{ fontWeight: 'medium', color: 'success.main' }}>
                                Active
                            </Typography>
                            <Chip 
                                label={activeTriggers.length} 
                                size='small' 
                                color='success' 
                                sx={{ ml: 1 }} 
                            />
                        </Box>
                        
                        {activeTriggers.length > 0 ? (
                            <List sx={{ py: 0 }}>
                                {activeTriggers.map(({trigger, eventType}, index) => (
                                    <ListItem 
                                        key={trigger.id || index}
                                        sx={{ 
                                            py: 1.5, 
                                            px: 0,
                                            borderBottom: index < activeTriggers.length - 1 ? '1px solid' : 'none',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <Event color='action' />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={eventType?.label || 'Unknown Event'}
                                            secondary={`Trigger ID: ${trigger.id}`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic' }}>
                                No events selected to start journey
                            </Typography>
                        )}
                    </CardContent>
                </Card>

                {/* Draft Triggers Section */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box display='flex' alignItems='center' sx={{ mb: 2 }}>
                            <Event sx={{ mr: 1, color: 'warning.main' }} />
                            <Typography variant='h6' sx={{ fontWeight: 'medium', color: 'warning.main' }}>
                                Draft
                            </Typography>
                            <Chip 
                                label={draftTriggers.length} 
                                size='small' 
                                color='warning' 
                                sx={{ ml: 1 }} 
                            />
                        </Box>
                        
                        {draftTriggers.length > 0 ? (
                            <List sx={{ py: 0 }}>
                                {draftTriggers.map(({trigger, eventType}, index) => (
                                    <ListItem 
                                        key={trigger.id || index}
                                        sx={{ 
                                            py: 1.5, 
                                            px: 0,
                                            borderBottom: index < draftTriggers.length - 1 ? '1px solid' : 'none',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <Event color='action' />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={eventType?.label || 'Unknown Event'}
                                            secondary={`Draft ID: ${trigger.id}`}
                                        />
                                        <IconButton 
                                            onClick={() => handleDeleteActionTrigger(trigger.id)}
                                            size='small'
                                            color='error'
                                            sx={{ ml: 1 }}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic' }}>
                                No draft events selected
                            </Typography>
                        )}
                    </CardContent>
                </Card>

                {/* Add New Event Trigger Section */}
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <FlowActionTriggerProfileEventForm
                        onSubmit={handleSubmitNewActionTrigger}
                        flowId={journey.id}
                        triggerType='event_query'
                        flowTravelActionType='enter'
                        journey={journey}
                        profileEventTypes={profileEventTypes}
                        noEvents={
                            <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                                No more unassigned events available for this journey
                            </Typography>
                        }
                    />
                </Paper>
            </Box>

            {/* Footer Actions */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                pt: 2, 
                borderTop: '1px solid', 
                borderColor: 'divider',
                mt: 2
            }}>
                <Button 
                    onClick={hideSidebar} 
                    variant='outlined'
                    sx={{ minWidth: 100 }}
                >
                    Close
                </Button>
            </Box>
        </Box>
    );



};

export default FlowTriggerFilter;
